/**
 * Saved matter templates — a person's own starting points.
 *
 * WHAT THEY ARE FOR: somebody who opens many matters for the same client, filling in the same
 * two-thirds of the form each time. Close to "copy an existing matter", but kept deliberately,
 * named, and not tied to one matter that might later be deleted.
 *
 * THEY ARE PRIVATE. These were readable firm-wide at first, on the theory that an intake suiting
 * one matter suits the next one whoever starts it. That was the wrong trade: a template is a copy
 * of a real matter, carrying a real client's name, address and contacts, and handing that to every
 * signed-in user to save somebody typing is not a bargain worth making.
 *
 * THE QUERY MUST FILTER ON createdBy. Firestore rejects a query whose results the rules cannot
 * guarantee, so with owner-scoped reads an unfiltered listing fails outright rather than quietly
 * returning fewer rows. The `where` below is load-bearing, not an optimisation.
 *
 * Both writes go through callables (saveTemplate / deleteTemplate) so they are audit-logged and
 * the ownership check cannot be bypassed from the client. The data stored had
 * stripPreventCloneFields applied in the browser before it was ever sent, so a field an
 * administrator flagged "never copy" is not merely hidden — it is absent.
 */

import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { CallStatus, useCallable } from '../lib/useCallable';
import { useCollection } from '../lib/useFirestore';
import { formatTs } from './MatterList';

export default function Templates() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { data: templates, loading } = useCollection(
    user ? 'templates' : null,
    // Load-bearing: see the note above. Without this the read is refused, not narrowed.
    [['where', 'createdBy', '==', user?.uid], ['orderBy', 'createdAt', 'desc']]
  );
  const { run, busy, error, saved } = useCallable();

  const use = (template) =>
    navigate('/matters/new', {
      state: {
        seedMatter: template.data || {},
        seedLabel: `Pre-filled from the template “${template.name}”.`,
      },
    });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold text-firm-700 dark:text-firm-100">Templates</h1>
        <CallStatus busy={busy} error={error} saved={saved} />
      </div>

      <p className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 text-xs text-slate-600 dark:text-slate-300">
        Save any intake as a template with <strong>Save as template</strong> at the bottom of
        the form — useful when you open several matters for the same client and fill in the same
        answers each time. <strong>Only you can see your templates.</strong> They never carry
        matter numbers, attachments, or any field an administrator flagged as “never copy”.
      </p>

      {loading ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">Loading templates…</p>
      ) : templates.length === 0 ? (
        <p className="rounded-lg bg-white dark:bg-slate-900 p-6 text-sm text-slate-500 dark:text-slate-400 shadow">
          No templates yet.
        </p>
      ) : (
        <table className="w-full overflow-hidden rounded-lg bg-white dark:bg-slate-900 text-sm shadow">
          <thead className="bg-slate-50 dark:bg-slate-800 text-left text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Created</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {templates.map((template) => (
              <tr key={template.id}>
                <td className="px-4 py-2">
                  <div className="font-medium text-slate-800 dark:text-slate-100">{template.name}</div>
                  {(template.sectionsPreview || []).length > 0 && (
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      {template.sectionsPreview.slice(0, 4).join(' · ')}
                    </div>
                  )}
                </td>
                <td className="px-4 py-2 text-xs text-slate-500 dark:text-slate-400">
                  {formatTs(template.createdAt)}
                </td>
                <td className="px-4 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => use(template)}
                    className="text-firm-700 dark:text-firm-100 hover:underline"
                  >
                    Start a matter
                  </button>
                  {(isAdmin || template.createdBy === user.uid) && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => run('deleteTemplate', { templateId: template.id })}
                      className="ml-3 text-xs text-red-600 dark:text-red-400 hover:underline disabled:opacity-40"
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
